// =============================================================================
// complianceReportPdf.ts — the company compliance report, as a PDF
// =============================================================================
// One document that answers, for a client, an auditor or a Monday morning:
// where does Coretel stand on site readiness, and who is holding it up.
//
// Shape, from James:
//   "each employee and their status / list of critical compliance licences and
//    inductions, then any random extras like a Job Fit or asbestos removal
//    training. With a summary on the top with some graphs and widgets showing
//    the status of the company this month."
//
// So: a summary page with the company numbers, a readiness chart and what is
// happening this month, then one block per person.
//
// EVERYTHING IS DERIVED AT THE MOMENT YOU PRESS THE BUTTON.
// Nothing is snapshotted or stored. Two reports a week apart differ because the
// facts differ, not because somebody forgot to refresh a spreadsheet. The date
// and time it was generated is stamped on every page for exactly that reason.
//
// WHY THE COLOURS ARE WHAT THEY ARE
// ---------------------------------
// The five status colours were checked with a colour-vision simulator rather
// than picked by eye. The worst adjacent pair (amber against green) separates
// at dE 15.9 under protanopia, which is comfortably clear. "Missing" is a
// deliberate low-chroma grey, not red: somebody with nothing loaded is a
// different problem from somebody whose ticket lapsed yesterday, and drawing
// both in red hides that.
//
// Colour is never the only signal anywhere in here - every status is written
// out in words in the table beside it, so the report survives a black-and-white
// photocopier, which is how half of these end up being read.
//
// Pattern mirrors vocCertPdf.ts: lazy-load pdfmake, build a docDefinition,
// createPdf().download().
// =============================================================================

// @ts-ignore — Vite ?url suffix isn't in pdfmake's type defs
import pdfmakeScriptUrl  from "pdfmake/build/pdfmake.min.js?url";
// @ts-ignore
import vfsFontsScriptUrl from "pdfmake/build/vfs_fonts.js?url";
import { supabase } from "@/lib/supabase";

// -----------------------------------------------------------------------------
// Palette — validated, not guessed
// -----------------------------------------------------------------------------
const BRAND = "#ff7844";
const INK   = "#111111";
const TEXT  = "#1a1a1a";
const MUTED = "#666666";
const DIM   = "#999999";
const RULE  = "#e5e5e5";
const WASH  = "#faf9f8";

type Status = "current" | "expiring" | "pending" | "expired" | "missing";

const STATUS: Record<Status, { label: string; fill: string; word: string }> = {
  current:  { label: "Current",      fill: "#127a4e", word: "Held and in date" },
  expiring: { label: "Expiring",     fill: "#d99400", word: "Valid, inside 60 days" },
  pending:  { label: "Not verified", fill: "#2563b8", word: "Uploaded, not yet checked" },
  expired:  { label: "Expired",      fill: "#c0392b", word: "Held, out of date" },
  missing:  { label: "Missing",      fill: "#b3b6ba", word: "Nothing on file" },
};

// Best to worst. The bar draws in this order and sorts use it.
const ORDER: Status[] = ["current", "expiring", "pending", "expired", "missing"];

// -----------------------------------------------------------------------------
// pdfmake loader (lazy, single-fetch) — same as vocCertPdf.ts
// -----------------------------------------------------------------------------
function loadScriptOnce(src: string, tag: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[data-tag="${tag}"]`);
    if (existing) {
      if (existing.dataset.loaded === "true") { resolve(); return; }
      existing.addEventListener("load",  () => resolve());
      existing.addEventListener("error", () => reject(new Error(`Failed to load ${tag}`)));
      return;
    }
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.dataset.tag = tag;
    script.onload  = () => { script.dataset.loaded = "true"; resolve(); };
    script.onerror = () => reject(new Error(`Failed to load ${tag} from ${src}`));
    document.head.appendChild(script);
  });
}

async function getPdfMake(): Promise<any> {
  const existing = (window as any).pdfMake;
  if (existing?.vfs && Object.keys(existing.vfs).length > 0) return existing;
  await loadScriptOnce(pdfmakeScriptUrl,  "pdfmake-core");
  await loadScriptOnce(vfsFontsScriptUrl, "pdfmake-vfs");
  return (window as any).pdfMake;
}

// -----------------------------------------------------------------------------
// Dates, formatted the way an Australian reads them
// -----------------------------------------------------------------------------
function d(iso: string | null | undefined): string {
  if (!iso) return "—";
  const dt = new Date(iso);
  if (isNaN(dt.getTime())) return "—";
  return dt.toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" });
}
function iso(dt: Date): string { return dt.toISOString().slice(0, 10); }
function addDays(n: number): string { const x = new Date(); x.setDate(x.getDate() + n); return iso(x); }

// -----------------------------------------------------------------------------
// The stacked bar, drawn as real rectangles
// -----------------------------------------------------------------------------
// 2pt of white between segments rather than a stroke. The gap does the
// separating; a border would add ink that is not data. Rounded ends are not
// available on a pdfmake rect at this height, so the bar is left square - a
// 6pt-high bar with fake corners reads as a rendering artefact.
// -----------------------------------------------------------------------------
function bar(counts: Record<string, number>, width: number, height = 7): any {
  const total = ORDER.reduce((n, s) => n + (counts[s] ?? 0), 0);
  if (!total) {
    return { canvas: [{ type: "rect", x: 0, y: 0, w: width, h: height, color: "#eeeeee" }] };
  }
  const parts = ORDER.map(s => ({ s, n: counts[s] ?? 0 })).filter(p => p.n > 0);
  const gap = 2;
  const usable = width - gap * (parts.length - 1);
  const shapes: any[] = [];
  let x = 0;
  parts.forEach(({ s, n }, i) => {
    const w = (usable * n) / total;
    shapes.push({ type: "rect", x, y: 0, w, h: height, color: STATUS[s].fill });
    x += w + (i < parts.length - 1 ? gap : 0);
  });
  return { canvas: shapes };
}

function legend(): any {
  return {
    columns: ORDER.map(s => ({
      width: "auto",
      columns: [
        { canvas: [{ type: "rect", x: 0, y: 2, w: 7, h: 7, color: STATUS[s].fill }], width: 9 },
        { text: STATUS[s].label, fontSize: 7, color: MUTED, margin: [3, 1, 10, 0] },
      ],
    })),
    margin: [0, 4, 0, 0],
  };
}

function widget(label: string, value: string, sub: string, tint = false): any {
  return {
    table: {
      widths: ["*"],
      body: [[{
        stack: [
          { text: label.toUpperCase(), fontSize: 6.5, color: MUTED, characterSpacing: 0.8 },
          { text: value, fontSize: 20, bold: true, color: tint ? BRAND : INK, margin: [0, 3, 0, 1] },
          { text: sub, fontSize: 7, color: DIM },
        ],
        border: [true, true, true, true],
        borderColor: [RULE, RULE, RULE, RULE],
        fillColor: WASH,
        margin: [8, 7, 8, 7],
      }]],
    },
    layout: {
      hLineColor: () => RULE, vLineColor: () => RULE,
      hLineWidth: () => 0.6, vLineWidth: () => 0.6,
    },
  };
}

// =============================================================================
// Data
// =============================================================================
interface ReqRow {
  employee_id: string; role_id: string; role_name: string; role_kind: string;
  alt_group: string; is_mandatory: boolean;
  licence_type_id: string; licence_code: string; licence_name: string;
  licence_id: string | null; expiry_date: string | null; status: Status;
}

async function loadEverything() {
  const [emp, excluded, sum, reqs, lic, types, asg, roles] = await Promise.all([
    // Active, and in scope for the site-readiness question. Somebody excluded is
    // not "0% compliant" - the question does not apply to them - and averaging
    // them into the company figure produces a headline nobody believes.
    supabase.from("employees")
      .select("employee_id, first_name, last_name, primary_role_name, job_title")
      .eq("is_active", true)
      .eq("in_site_compliance", true)
      .order("first_name"),
    supabase.from("employees")
      .select("employee_id", { count: "exact", head: true })
      .eq("is_active", true)
      .eq("in_site_compliance", false),
    supabase.from("v_employee_compliance_summary").select("*"),
    supabase.from("v_employee_requirements").select("*"),
    supabase.from("employee_licences")
      .select("employee_id, licence_type_id, licence_number, expiry_date, verified_at, issue_date")
      .eq("is_active", true).is("superseded_at", null),
    supabase.from("licence_types").select("licence_type_id, code, display_name, category"),
    supabase.from("employee_roles").select("employee_id, role_id, grade"),
    supabase.from("compliance_roles")
      .select("role_id, code, display_name, kind, is_active, is_baseline, grade_family"),
  ]);

  const firstError = [emp, sum, reqs, lic, types, asg, roles].find(r => r.error)?.error;
  if (firstError) throw new Error(firstError.message);

  return {
    excludedCount: (excluded as any).count ?? 0,
    employees: (emp.data ?? []) as any[],
    summaries: (sum.data ?? []) as any[],
    reqs:      (reqs.data ?? []) as unknown as ReqRow[],
    licences:  (lic.data ?? []) as any[],
    types:     (types.data ?? []) as any[],
    assigned:  (asg.data ?? []) as any[],
    roles:     (roles.data ?? []) as any[],
  };
}

// Alternatives resolve to their best member — holding any one satisfies the group.
function bestOf(list: Status[]): Status {
  for (const s of ORDER) if (list.includes(s)) return s;
  return "missing";
}

// =============================================================================
// The document
// =============================================================================
export async function downloadComplianceReportPdf(): Promise<void> {
  const data = await loadEverything();
  const pdfMake = await getPdfMake();

  const now = new Date();
  const typeBy = new Map(data.types.map(t => [t.licence_type_id, t]));
  const roleBy = new Map(data.roles.map(r => [r.role_id, r]));
  const sumBy  = new Map(data.summaries.map(s => [s.employee_id, s]));

  const soon = addDays(60);
  const thisMonth = addDays(30);
  const today = iso(now);

  // ---- per person -----------------------------------------------------------
  const people = data.employees.map(e => {
    const mine = data.reqs.filter(r => r.employee_id === e.employee_id);

    // Collapse to one entry per REQUIREMENT, not per role.
    //
    // Two passes. First gather the options of each (role, alt_group) pair; then
    // merge pairs that resolve to the same set of tickets, so a ticket two roles
    // both need is one line, not two. Two roles wanting the same thing is not
    // two problems, and printing it twice makes a report look padded.
    const pairs = new Map<string, {
      opts: Map<string, ReqRow>; role: string; kind: string; mand: boolean;
    }>();
    for (const r of mine) {
      const k = `${r.role_id}::${r.alt_group}`;
      const e = pairs.get(k)
        ?? { opts: new Map<string, ReqRow>(), role: r.role_name, kind: r.role_kind, mand: r.is_mandatory };
      e.opts.set(r.licence_type_id, r);
      e.mand = e.mand || r.is_mandatory;
      pairs.set(k, e);
    }

    const KIND_RANK: Record<string, number> = { primary: 1, task: 2, plant: 3, access: 4 };
    const merged = new Map<string, {
      roles: string[]; role_kind: string; is_mandatory: boolean;
      options: ReqRow[]; status: Status; expiry: string | null;
    }>();
    for (const e of pairs.values()) {
      const options = Array.from(e.opts.values())
        .sort((a, b) => a.licence_name.localeCompare(b.licence_name));
      const key = options.map(o => o.licence_type_id).join("|");
      const g = merged.get(key) ?? {
        roles: [], role_kind: e.kind, is_mandatory: false,
        options, status: "missing" as Status, expiry: null,
      };
      if (!g.roles.includes(e.role)) g.roles.push(e.role);
      g.is_mandatory = g.is_mandatory || e.mand;
      if ((KIND_RANK[e.kind] ?? 9) < (KIND_RANK[g.role_kind] ?? 9)) g.role_kind = e.kind;
      merged.set(key, g);
    }
    for (const g of merged.values()) {
      g.status = bestOf(g.options.map(o => o.status));
      g.expiry = g.options.find(o => o.status === g.status)?.expiry_date ?? null;
      g.roles.sort();
    }
    const list = Array.from(merged.values());

    const counts: Record<string, number> = {};
    for (const g of list) if (g.is_mandatory) counts[g.status] = (counts[g.status] ?? 0) + 1;

    // Extras: live tickets on file that no role of theirs asks for. Job Fit,
    // asbestos removal, a forklift licence somebody happens to hold. Worth
    // reporting - a client asking "who can do X" is often asking about these.
    const required = new Set(mine.map(r => r.licence_type_id));
    const extras = data.licences
      .filter(l => l.employee_id === e.employee_id && !required.has(l.licence_type_id))
      .map(l => ({ ...l, type: typeBy.get(l.licence_type_id) }))
      .filter(l => l.type)
      .sort((a, b) => (a.type.display_name as string).localeCompare(b.type.display_name));

    const s = sumBy.get(e.employee_id);
    const pct: number | null = s?.pct_compliant ?? null;
    const mandatory = list.filter(g => g.is_mandatory).length;

    const roleNames = data.assigned
      .filter(a => a.employee_id === e.employee_id)
      .map(a => roleBy.get(a.role_id))
      .filter(r => r && r.is_active && !r.is_baseline)
      .map(r => r.display_name as string)
      .sort();

    return { e, list, counts, extras, pct, mandatory, roleNames, s };
  });

  // Worst first. The report exists to surface problems.
  people.sort((a, b) => (a.pct ?? -1) - (b.pct ?? -1)
    || `${a.e.first_name} ${a.e.last_name}`.localeCompare(`${b.e.first_name} ${b.e.last_name}`));

  // ---- company totals -------------------------------------------------------
  const company: Record<string, number> = {};
  for (const p of people) for (const k of ORDER) company[k] = (company[k] ?? 0) + (p.counts[k] ?? 0);

  const ready    = people.filter(p => p.mandatory > 0 && (p.pct ?? 0) >= 100 && !(p.counts.expiring)).length;
  const renewals = people.filter(p => p.mandatory > 0 && (p.pct ?? 0) >= 100 && !!p.counts.expiring).length;
  const notReady = people.filter(p => p.mandatory > 0 && (p.pct ?? 0) < 100).length;
  const unset    = people.filter(p => p.roleNames.length === 0).length;

  // ---- what is happening this month ----------------------------------------
  const movements: { who: string; what: string; when: string; state: Status }[] = [];
  for (const p of people) {
    for (const g of p.list) {
      if (!g.is_mandatory) continue;
      if (g.status === "expired") {
        movements.push({
          who: `${p.e.first_name} ${p.e.last_name}`,
          what: g.options.map(o => o.licence_name).join(" or "),
          when: g.expiry ?? "", state: "expired",
        });
      } else if (g.status === "expiring" && g.expiry && g.expiry <= thisMonth) {
        movements.push({
          who: `${p.e.first_name} ${p.e.last_name}`,
          what: g.options.map(o => o.licence_name).join(" or "),
          when: g.expiry, state: "expiring",
        });
      }
    }
  }
  movements.sort((a, b) => (a.when || "9999").localeCompare(b.when || "9999"));

  const expiringLater = people.reduce((n, p) => n + (p.counts.expiring ?? 0), 0);

  // ===========================================================================
  // Build it
  // ===========================================================================
  const content: any[] = [];

  // ---- title ----------------------------------------------------------------
  content.push(
    {
      columns: [
        {
          stack: [
            { text: "CORETEL GROUP", fontSize: 8, bold: true, color: BRAND, characterSpacing: 1.4 },
            { text: "Site readiness and compliance report", fontSize: 17, bold: true, color: INK, margin: [0, 3, 0, 0] },
            {
              text: `Worked out from the tickets on file at `
                    + `${now.toLocaleString("en-AU", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}`,
              fontSize: 8, color: MUTED, margin: [0, 3, 0, 0],
            },
          ],
        },
      ],
      margin: [0, 0, 0, 12],
    },
    { canvas: [{ type: "line", x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 1.4, lineColor: BRAND }], margin: [0, 0, 0, 14] },
  );

  // ---- widgets --------------------------------------------------------------
  content.push({
    columns: [
      widget("Site ready", String(ready), "everything current"),
      { width: 8, text: "" },
      widget("Renewals due", String(renewals), "ready, but expiring", renewals > 0),
      { width: 8, text: "" },
      widget("Not site ready", String(notReady), "gaps to close", notReady > 0),
      { width: 8, text: "" },
      widget("Not set up", String(unset), "no roles recorded", unset > 0),
    ],
    margin: [0, 0, 0, 14],
  });

  // ---- company bar ----------------------------------------------------------
  const totalReq = ORDER.reduce((n, s) => n + (company[s] ?? 0), 0);
  const companyPct = totalReq
    ? Math.round((100 * ((company.current ?? 0) + (company.expiring ?? 0))) / totalReq) : 0;

  content.push(
    { text: "Company readiness", fontSize: 11, bold: true, color: INK, margin: [0, 0, 0, 2] },
    {
      text: `${(company.current ?? 0) + (company.expiring ?? 0)} of ${totalReq} requirements met across `
            + `${people.length} ${people.length === 1 ? "person" : "people"} — ${companyPct}%`,
      fontSize: 8, color: MUTED, margin: [0, 0, 0, 6],
    },
    bar(company, 515, 10),
    legend(),
    { text: "", margin: [0, 0, 0, 14] },
  );

  // ---- per-person chart -----------------------------------------------------
  content.push(
    { text: "Everyone, worst first", fontSize: 11, bold: true, color: INK, margin: [0, 0, 0, 6] },
    {
      table: {
        widths: [110, 30, "*"],
        body: people.map(p => ([
          { text: `${p.e.first_name} ${p.e.last_name}`, fontSize: 8, color: TEXT, margin: [0, 2, 0, 0] },
          {
            text: p.pct == null ? "—" : `${p.pct}%`,
            fontSize: 8, bold: true, alignment: "right",
            color: p.pct == null ? DIM : p.pct >= 100 ? STATUS.current.fill : p.pct >= 50 ? STATUS.expiring.fill : STATUS.expired.fill,
            margin: [0, 2, 6, 0],
          },
          { ...bar(p.counts, 360, 7), margin: [0, 3, 0, 0] },
        ])),
      },
      layout: "noBorders",
      margin: [0, 0, 0, 14],
    },
  );

  // ---- this month -----------------------------------------------------------
  content.push(
    { text: "Needs attention this month", fontSize: 11, bold: true, color: INK, margin: [0, 0, 0, 2] },
    {
      text: movements.length
        ? `${movements.filter(m => m.state === "expired").length} expired, `
          + `${movements.filter(m => m.state === "expiring").length} expiring within 30 days. `
          + `${expiringLater} expiring within 60.`
        : `Nothing expired and nothing expiring within 30 days. ${expiringLater} expiring within 60.`,
      fontSize: 8, color: MUTED, margin: [0, 0, 0, 6],
    },
  );

  if (movements.length) {
    content.push({
      table: {
        headerRows: 1,
        widths: [110, "*", 62, 52],
        body: [
          ["Who", "Ticket", "Date", "State"].map(h => ({
            text: h, fontSize: 7, bold: true, color: MUTED, characterSpacing: 0.5,
          })),
          ...movements.slice(0, 24).map(m => ([
            { text: m.who, fontSize: 8, color: TEXT },
            { text: m.what, fontSize: 8, color: TEXT },
            { text: d(m.when), fontSize: 8, color: TEXT },
            { text: STATUS[m.state].label, fontSize: 8, bold: true, color: STATUS[m.state].fill },
          ])),
        ],
      },
      layout: {
        hLineWidth: (i: number) => (i === 1 ? 0.8 : 0.4),
        vLineWidth: () => 0,
        hLineColor: (i: number) => (i === 1 ? RULE : "#f2f2f2"),
        paddingTop: () => 4, paddingBottom: () => 4,
        paddingLeft: () => 0, paddingRight: () => 6,
      },
      margin: [0, 0, 0, 6],
    });
    if (movements.length > 24) {
      content.push({
        text: `…and ${movements.length - 24} more. The full list is on each person's page below — nothing has been dropped from the detail.`,
        fontSize: 7, italics: true, color: DIM,
      });
    }
  }

  // ---- one block per person -------------------------------------------------
  people.forEach((p, idx) => {
    const KIND_LABEL: Record<string, string> = {
      primary: "Main role", task: "Signed off for", plant: "Plant and vehicles", access: "Site access",
    };
    const KIND_ORDER = ["primary", "task", "plant", "access"];

    const byKind = new Map<string, typeof p.list>();
    for (const g of p.list) byKind.set(g.role_kind, [...(byKind.get(g.role_kind) ?? []), g] as any);

    const body: any[] = [];
    for (const kind of KIND_ORDER) {
      const list = (byKind.get(kind) ?? []) as typeof p.list;
      if (!list.length) continue;
      list.sort((a, b) => ORDER.indexOf(b.status) - ORDER.indexOf(a.status)
        || a.options[0].licence_name.localeCompare(b.options[0].licence_name));

      body.push([{
        text: KIND_LABEL[kind] ?? kind, fontSize: 7, bold: true, color: MUTED,
        characterSpacing: 0.5, colSpan: 4, margin: [0, 6, 0, 2],
      }, {}, {}, {}]);

      for (const g of list) {
        const extra = g.is_mandatory ? "" : "   (recommended, not required)";

        if (g.options.length === 1) {
          const o = g.options[0];
          body.push([
            { text: o.licence_name + extra, fontSize: 8, color: g.is_mandatory ? TEXT : MUTED },
            { text: g.roles.join(", "), fontSize: 7.5, color: MUTED },
            { text: g.expiry ? d(g.expiry) : "—", fontSize: 8, color: TEXT },
            // The word, always. Colour alone would not survive a photocopier.
            { text: STATUS[g.status].label, fontSize: 8, bold: true, color: STATUS[g.status].fill },
          ]);
          continue;
        }

        // An alternative group: the verdict, then every option with its own
        // status. Somebody holding the under-11m EWP ticket but not the
        // high-risk one is not a failure - it is a limit on what they can
        // operate, and a report that hides which one they hold is no use to
        // whoever is deciding who goes to site.
        body.push([
          { text: "Any one of the following" + extra, fontSize: 8, italics: true, color: MUTED },
          { text: g.roles.join(", "), fontSize: 7.5, color: MUTED },
          { text: g.expiry ? d(g.expiry) : "—", fontSize: 8, color: TEXT },
          { text: STATUS[g.status].label, fontSize: 8, bold: true, color: STATUS[g.status].fill },
        ]);
        for (const o of g.options) {
          body.push([
            { text: o.licence_name, fontSize: 8, color: TEXT, margin: [12, 0, 0, 0] },
            { text: "", fontSize: 7.5 },
            { text: o.expiry_date ? d(o.expiry_date) : "—", fontSize: 8, color: TEXT },
            { text: STATUS[o.status].label, fontSize: 8, color: STATUS[o.status].fill },
          ]);
        }
      }
    }

    if (p.extras.length) {
      body.push([{
        text: "Also on file", fontSize: 7, bold: true, color: MUTED,
        characterSpacing: 0.5, colSpan: 4, margin: [0, 6, 0, 2],
      }, {}, {}, {}]);
      for (const x of p.extras) {
        const st: Status = !x.expiry_date ? (x.verified_at ? "current" : "pending")
          : x.expiry_date < today ? "expired"
          : !x.verified_at ? "pending"
          : x.expiry_date <= soon ? "expiring" : "current";
        body.push([
          { text: x.type.display_name, fontSize: 8, color: TEXT },
          { text: `not required by any role · ${x.type.category ?? ""}`.trim(), fontSize: 7.5, color: DIM },
          { text: d(x.expiry_date), fontSize: 8, color: TEXT },
          { text: STATUS[st].label, fontSize: 8, bold: true, color: STATUS[st].fill },
        ]);
      }
    }

    if (!body.length) {
      body.push([{
        text: "No roles recorded, so nothing is being measured. Set this person up on the compliance matrix.",
        fontSize: 8, italics: true, color: MUTED, colSpan: 4,
      }, {}, {}, {}]);
    }

    content.push({
      // Keeps a person's block from being split mid-table where it can be.
      unbreakable: body.length <= 16,
      pageBreak: idx === 0 ? "before" : undefined,
      stack: [
        {
          columns: [
            {
              stack: [
                { text: `${p.e.first_name} ${p.e.last_name}`, fontSize: 12, bold: true, color: INK },
                {
                  text: p.roleNames.length ? p.roleNames.join(" · ")
                        : (p.e.job_title ?? p.e.primary_role_name ?? "No roles recorded"),
                  fontSize: 7.5, color: MUTED, margin: [0, 2, 0, 0],
                },
              ],
            },
            {
              width: 150,
              stack: [
                {
                  text: p.pct == null ? "—" : `${p.pct}%`,
                  fontSize: 15, bold: true, alignment: "right",
                  color: p.pct == null ? DIM : p.pct >= 100 ? STATUS.current.fill : p.pct >= 50 ? STATUS.expiring.fill : STATUS.expired.fill,
                },
                {
                  text: `${(p.counts.current ?? 0) + (p.counts.expiring ?? 0)} of ${p.mandatory} met`,
                  fontSize: 7, color: MUTED, alignment: "right", margin: [0, 1, 0, 0],
                },
              ],
            },
          ],
          margin: [0, 12, 0, 4],
        },
        { ...bar(p.counts, 515, 6), margin: [0, 0, 0, 4] },
        {
          table: { headerRows: 0, widths: ["*", 118, 62, 52], body },
          layout: {
            hLineWidth: () => 0.4, vLineWidth: () => 0,
            hLineColor: () => "#f2f2f2",
            paddingTop: () => 3, paddingBottom: () => 3,
            paddingLeft: () => 0, paddingRight: () => 6,
          },
        },
      ],
    });
  });

  // ---- what the report is, and is not --------------------------------------
  content.push({
    pageBreak: "before",
    stack: [
      { text: "How to read this", fontSize: 11, bold: true, color: INK, margin: [0, 0, 0, 6] },
      {
        ul: ORDER.map(s => ({
          text: [
            { text: STATUS[s].label, bold: true, color: STATUS[s].fill },
            { text: ` — ${STATUS[s].word}.`, color: TEXT },
          ],
          fontSize: 8.5, margin: [0, 0, 0, 2],
        })),
        margin: [0, 0, 0, 10],
      },
      {
        text: [
          { text: "Expiring counts as compliant. ", bold: true },
          "A ticket valid for another three weeks is valid today. It is shown separately so it "
          + "gets chased, but it is not counted as a failure — a number that cries wolf stops "
          + "being believed.",
        ],
        fontSize: 8.5, color: TEXT, margin: [0, 0, 0, 6],
      },
      {
        text: [
          { text: "A percentage is measured against that person's own roles. ", bold: true },
          "Somebody with no roles recorded is measured only against the baseline every field "
          + "worker holds, so 100% for them means \"meets the baseline\", not \"ready for anything\". "
          + "Those people are listed as not set up at the top.",
        ],
        fontSize: 8.5, color: TEXT, margin: [0, 0, 0, 6],
      },
      {
        text: [
          { text: "Nothing here is stored. ", bold: true },
          "Every figure was worked out from the licence records at the moment this file was "
          + "generated. Regenerate it and it will be right again.",
        ],
        fontSize: 8.5, color: TEXT, margin: [0, 0, 0, 6],
      },
      {
        text: data.excludedCount > 0
          ? [
              { text: "Who is counted. ", bold: true },
              `This report covers the ${people.length} current employees who attend site. `
              + `${data.excludedCount} further `
              + `${data.excludedCount === 1 ? "person is" : "people are"} on the employee list `
              + "but marked as not attending site — office staff and similar — and are left out "
              + "of every figure above. People who have left the company are not included.",
            ]
          : [
              { text: "Who is counted. ", bold: true },
              `Every one of the ${people.length} current employees who attend site. `
              + "People who have left the company are not included.",
            ],
        fontSize: 8.5, color: TEXT,
      },
    ],
  });

  const docDefinition: any = {
    pageSize: "A4",
    pageMargins: [40, 40, 40, 46],
    defaultStyle: { font: "Roboto", fontSize: 9, color: TEXT },
    content,
    footer: (page: number, total: number) => ({
      margin: [40, 10, 40, 0],
      columns: [
        {
          text: `Coretel Group — site readiness, generated ${d(iso(now))} from the licence records held in Coretel Ops.`,
          fontSize: 6.5, color: DIM,
        },
        { text: `${page} of ${total}`, fontSize: 6.5, color: DIM, alignment: "right", width: 60 },
      ],
    }),
  };

  pdfMake.createPdf(docDefinition)
    .download(`Coretel_Compliance_Report_${iso(now)}.pdf`);
}
